import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  ArrowUpRight,
  BrainCircuit,
  Calculator,
  Check,
  ChevronDown,
  FileCheck2,
  HeartPulse,
  Menu,
  Mic2,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Sparkles,
  X,
} from 'lucide-react'

const scaleGroups = [
  { label: 'Инсульт', scales: ['NIHSS', 'mRS', 'Индекс Бартела', 'GUSS', 'MAS'] },
  { label: 'Логопедия', scales: ['ТДА', 'АТА (Вассерман)', 'Frenchay', 'DOSS', 'Тест рисования часов'] },
  { label: 'Деменции', scales: ['MoCA', 'MMSE', 'FAB', 'GDS', 'CDR', 'Опросник ФАД', 'Индекс Хачинского'] },
  { label: 'Паркинсон', scales: ['UPDRS', 'Шкала Хен и Яра', 'Шкала Шваба и Ингланда'] },
  { label: 'Склерозы', scales: ['ALSFRS-R', 'EDSS (Курцке)'] },
]

const doctorFeatures = [
  { icon: ScanLine, title: 'Распознавание в реальном времени', body: 'AI слушает и анализирует ответы во время теста.' },
  { icon: Calculator, title: 'Автоматический подсчет баллов', body: 'Точная оценка по NIHSS, MoCA, FAB и другим шкалам.' },
  { icon: FileCheck2, title: 'Готовый отчет за секунды', body: 'Протокол для карты и график динамики без ручной работы.' },
]

const patientFeatures = [
  { icon: Sparkles, title: 'Удобный AI-тренажер', body: 'Домашние занятия с понятными подсказками.' },
  { icon: Activity, title: 'Мгновенная обратная связь', body: 'Пациент видит прогресс после каждого упражнения.' },
  { icon: ShieldCheck, title: 'Приватность и безопасность данных', body: 'Защищенный доступ для пациента и опекуна.' },
]

function FeatureList({ items }) {
  return (
    <div className="flex flex-col">
      {items.map((item) => (
        <div key={item.title} className="flex gap-4 border-t border-border py-4 first:border-t-0 first:pt-0 last:pb-0">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <item.icon className="size-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-primary">{item.title}</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{item.body}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function LandingPage() {
  const [activeScale, setActiveScale] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'ru')

  const selected = scaleGroups[activeScale]
  const closeMenu = () => setMenuOpen(false)

  const changeLang = (newLang) => {
    localStorage.setItem('lang', newLang)
    setLang(newLang)
  }

  return (
    <main id="top" className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 lg:px-8">
          
          <a href="#top" className="flex items-center gap-2.5" aria-label="Cortivox.ai на главную">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-primary text-primary-foreground"><BrainCircuit className="size-4" /></span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-primary">Cortivox<span className="text-accent">.ai</span></span>
          </a>
          
          <div className="hidden items-center gap-6 lg:flex">
            <nav className="flex items-center gap-6 text-xs font-medium text-muted-foreground" aria-label="Основная навигация">
              <a href="#doctors" className="transition-colors hover:text-primary">Для Врачей</a>
              <a href="#patients" className="transition-colors hover:text-primary">Для Пациентов</a>
              <Link to="/scales" className="transition-colors hover:text-primary">Шкалы</Link>
              <Link to="/exercises" className="transition-colors hover:text-primary">Тренажер</Link>
              <a href="#about" className="transition-colors hover:text-primary">О Платформе</a>
            </nav>
            
            <div className="relative">
              <select 
                value={lang} 
                onChange={(e) => changeLang(e.target.value)}
                className="appearance-none bg-transparent border border-border rounded-full pl-3 pr-7 py-1.5 text-xs font-medium text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 cursor-pointer"
              >
                <option value="ru">Русский</option>
                <option value="en">English</option>
                <option value="uz">O'zbekcha</option>
                <option value="kz">Қазақша</option>
                <option value="es">Español</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            </div>

            {/* НОВОЕ: Кнопки входа для Десктопа */}
            <div className="flex items-center gap-3">
              <Link to="/doctor" className="rounded-full border border-border px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-secondary">
                Врач
              </Link>
              <Link to="/patient" className="rounded-full bg-accent px-4 py-2.5 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5">
                Пациент
              </Link>
            </div>
            {/* КОНЕЦ НОВОГО */}

            <a href="mailto:hello@cortivox.ai" className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">Запросить демо <ArrowUpRight className="size-3.5" /></a>
          </div>

          <button type="button" className="rounded-lg p-2 text-primary lg:hidden" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}</button>
        </div>
        
                {menuOpen && (
          <nav className="flex flex-col gap-4 border-t border-border px-5 py-5 text-sm font-medium text-primary lg:hidden" aria-label="Мобильная навигация">
            <a href="#doctors" onClick={closeMenu}>Для Врачей</a>
            <a href="#patients" onClick={closeMenu}>Для Пациентов</a>
            <Link to="/scales" onClick={closeMenu}>Шкалы</Link>
            {/* НОВОЕ: Добавлена ссылка на Тренажер */}
            <Link to="/exercises" onClick={closeMenu}>Тренажер</Link>
            <a href="#about" onClick={closeMenu}>О Платформе</a>
            
            {/* НОВОЕ: Кнопки входа для Мобильного меню */}
            <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-border">
              <Link to="/doctor" onClick={closeMenu} className="inline-flex w-fit rounded-full border border-border px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-secondary">
                Войти как Врач
              </Link>
              <Link to="/patient" onClick={closeMenu} className="inline-flex w-fit rounded-full bg-accent px-4 py-2.5 text-xs font-semibold text-white">
                Войти как Пациент
              </Link>
            </div>
            {/* КОНЕЦ НОВОГО */}

            <a href="mailto:hello@cortivox.ai" onClick={closeMenu} className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground">Запросить демо <ArrowUpRight className="size-3.5" /></a>
            
            <div className="relative pt-4 mt-2 border-t border-border">
              <select 
                value={lang} 
                onChange={(e) => changeLang(e.target.value)}
                className="appearance-none w-full bg-transparent border border-border rounded-full pl-3 pr-7 py-2 text-xs font-medium text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 cursor-pointer"
              >
                <option value="ru">Русский</option>
                <option value="en">English</option>
                <option value="uz">O'zbekcha</option>
                <option value="kz">Қазақша</option>
                <option value="es">Español</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 mt-2 -translate-y-1/2 size-4 text-muted-foreground" />
            </div>
          </nav>
        )}
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-8 px-5 pb-10 pt-10 sm:pb-12 lg:grid-cols-[1.04fr_.96fr] lg:px-8 lg:pb-14 lg:pt-14">
        <div>
          <p className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[.18em] text-accent"><span className="size-1.5 rounded-full bg-accent" /> Клинический интеллект</p>
          <h1 className="max-w-2xl text-balance text-[2.7rem] font-semibold leading-[1.05] tracking-[-0.06em] text-primary sm:text-5xl lg:text-[3.8rem]">Умная платформа нейропсихологической оценки и реабилитации</h1>
          <p className="mt-5 max-w-lg text-pretty text-sm leading-6 text-muted-foreground sm:text-base">Клинические шкалы и AI-тренажеры для неврологов и логопедов. Распознавание ответов пациента в реальном времени.</p>
          
          {/* НОВОЕ: Большие кнопки призыва к действию в Hero-секции */}
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/patient" className="inline-flex items-center justify-center rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5">
              Начать тренировку (Пациент)
            </Link>
            <Link to="/doctor" className="inline-flex items-center justify-center rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-primary shadow-sm transition-colors hover:bg-secondary">
              Провести тест (Врач)
            </Link>
          </div>
          {/* КОНЕЦ НОВОГО */}

        </div>
        <div className="relative mx-auto w-full max-w-[430px] lg:justify-self-end">
          <div className="absolute -inset-8 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[1.65rem] border border-primary/10 bg-secondary/70 p-3 shadow-xl shadow-primary/10">
            <div className="overflow-hidden rounded-[1.1rem] border border-border bg-background">
              <div className="flex items-center justify-between border-b border-border px-4 py-3"><div className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-accent" /><span className="text-[10px] font-semibold tracking-wide text-primary">CORTIVOX AI</span></div><span className="flex items-center gap-1 text-[9px] text-accent"><span className="size-1 animate-pulse rounded-full bg-accent" /> LIVE</span></div>
              <div className="grid gap-3 p-4 sm:grid-cols-[1.15fr_.85fr] sm:p-5">
                <div><div className="flex items-start justify-between"><div><p className="text-[9px] uppercase tracking-[.15em] text-muted-foreground">Текущая оценка</p><h2 className="mt-1 text-sm font-semibold text-primary">Речевая диагностика</h2></div><Mic2 className="size-4 text-accent" /></div><div className="mt-5 rounded-xl bg-primary p-3.5 text-primary-foreground"><div className="flex justify-between text-[9px] text-primary-foreground/65"><span>Распознавание речи</span><span>96%</span></div><div className="mt-2.5 flex h-11 items-center gap-1">{[25,42,58,35,68,45,75,50,63,38,55,78,42,60,32,52].map((height, index) => <span key={index} className="flex-1 rounded-full bg-accent" style={{ height: `${height}%`, opacity: index % 4 === 0 ? .55 : 1 }} />)}</div><div className="mt-2.5 flex items-center gap-1.5 text-[9px] text-primary-foreground/60"><span className="size-1 rounded-full bg-accent" /> Анализ ответа в реальном времени</div></div><div className="mt-3 flex items-center justify-between rounded-lg border border-border px-3 py-2.5"><span className="text-[9px] text-muted-foreground">Прогресс теста</span><span className="text-xs font-semibold text-primary">07:42</span></div></div>
                <div className="rounded-xl border border-border bg-secondary/50 p-3.5"><div className="flex items-center justify-between"><p className="text-[9px] uppercase tracking-[.15em] text-muted-foreground">Динамика</p><Activity className="size-3.5 text-accent" /></div><div className="mt-5 flex h-20 items-end gap-1.5">{[32,48,39,58,51,70,62,82].map((height, index) => <span key={index} className="flex-1 rounded-t-sm bg-accent/70" style={{ height: `${height}%`, opacity: index === 7 ? 1 : .5 + index * .06 }} />)}</div><div className="mt-4 border-t border-border pt-3"><p className="text-xl font-semibold tracking-tight text-primary">+24%</p><p className="mt-0.5 text-[9px] text-muted-foreground">улучшение за 4 недели</p></div></div>
              </div>
              <div className="flex items-center gap-1.5 border-t border-border px-4 py-2.5 text-[9px] text-muted-foreground"><ShieldCheck className="size-3 text-accent" /> Данные защищены</div>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="mx-auto max-w-6xl scroll-mt-24 px-5 pb-10 lg:px-8 lg:pb-12">
        <div className="grid overflow-hidden rounded-2xl border border-border bg-background md:grid-cols-2">
          <article id="doctors" className="scroll-mt-24 border-b border-border p-5 sm:p-7 md:border-b-0 md:border-r"><div className="mb-5 flex items-center gap-2"><span className="flex size-8 items-center justify-center rounded-lg bg-accent/10 text-accent"><HeartPulse className="size-4" /></span><h2 className="text-lg font-semibold tracking-[-.03em] text-primary">Для Врачей</h2></div><FeatureList items={doctorFeatures} /></article>
          <article id="patients" className="scroll-mt-24 bg-secondary/35 p-5 sm:p-7"><div className="mb-5 flex items-center gap-2"><span className="flex size-8 items-center justify-center rounded-lg bg-primary/8 text-primary"><Smartphone className="size-4" /></span><h2 className="text-lg font-semibold tracking-[-.03em] text-primary">Для Пациентов</h2></div><FeatureList items={patientFeatures} /></article>
        </div>
      </section>

      <section id="scales" className="mx-auto max-w-6xl scroll-mt-24 px-5 pb-10 lg:px-8 lg:pb-12"><div className="flex flex-col gap-4 rounded-2xl border border-border bg-secondary/35 p-5 sm:p-7 md:flex-row md:items-center md:justify-between"><div className="max-w-sm"><p className="text-[11px] font-semibold uppercase tracking-[.18em] text-accent">Клинический инструментарий</p><h2 className="mt-2 text-xl font-semibold tracking-[-.04em] text-primary">Полная база клинических шкал по нозологиям</h2></div><div className="w-full max-w-2xl"><div className="flex flex-wrap gap-2" role="tablist" aria-label="Нозологии">{scaleGroups.map((group, index) => <button key={group.label} type="button" role="tab" aria-selected={index === activeScale} onClick={() => setActiveScale(index)} className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-all ${index === activeScale ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:border-accent/50 hover:text-primary'}`}>{group.label}</button>)}</div><div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" aria-live="polite"><Check className="size-3.5 text-accent" /><span>{selected.scales.join(' · ')}</span></div></div></div></section>

      <footer className="border-t border-border px-5 py-5 lg:px-8"><div className="mx-auto flex max-w-6xl flex-col gap-4 text-xs sm:flex-row sm:items-center sm:justify-between"><div className="flex items-center gap-2 text-primary"><span className="flex size-7 items-center justify-center rounded-lg bg-primary text-primary-foreground"><BrainCircuit className="size-3.5" /></span><span className="font-semibold tracking-[-.03em]">Cortivox<span className="text-accent">.ai</span></span></div><p className="text-muted-foreground">© 2026 Cortivox.ai</p><div className="flex flex-wrap gap-4 text-muted-foreground"><a href="#" className="transition-colors hover:text-primary">Telegram</a><a href="mailto:hello@cortivox.ai" className="transition-colors hover:text-primary">Email</a><a href="#" className="transition-colors hover:text-primary">Политика конфиденциальности</a></div></div></footer>
    </main>
  )
}